import { ArrowRight, ExternalLink, X } from 'lucide-react';
import type {
  ConnectionEndpoint,
  InterfaceLagInfo,
  InterfaceVlanInfo,
} from '../hooks/useTopology';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { LinkTrafficSection } from './traffic/LinkTrafficSection';
import type { TrafficRange } from './traffic/range';

interface LinkData {
  id: string;
  connectionType: string;
  confidenceScore: number;
  sourceEndpoint: ConnectionEndpoint;
  targetEndpoint: ConnectionEndpoint;
  physical?: {
    speed: string;
    speedBps?: number;
  };
}

interface LinkDetailPanelProps {
  linkData: LinkData | null;
  /** Range inherited from the page, so the panel and the canvas agree. */
  trafficRange?: TrafficRange;
  onClose?: () => void;
  onDeviceSelect?: (endpoint: ConnectionEndpoint) => void;
}

function formatSpeed(speedMbps: number | undefined): string {
  if (speedMbps === undefined) return 'Unknown';
  if (speedMbps >= 1000) return `${(speedMbps / 1000).toFixed(0)} Gbps`;
  return `${String(speedMbps)} Mbps`;
}

function getConnectionTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    lldp: 'LLDP discovery',
    cdp: 'CDP discovery',
    arp: 'ARP discovery',
    manual: 'Manual',
  };
  return labels[type.toLowerCase()] || type;
}

function getVlanBadgeClass(vlan: InterfaceVlanInfo): string {
  if (vlan.isNative) return 'bg-sev-4/15 text-sev-4';
  if (vlan.isTagged) return 'bg-sev-5/15 text-sev-5';
  return 'bg-muted text-muted-foreground';
}

function formatVlanName(vlan: InterfaceVlanInfo): string {
  const label = `VLAN ${String(vlan.vlanId)}`;
  return vlan.isNative ? `${label} (native)` : label;
}

interface EndpointCardProps {
  endpoint: ConnectionEndpoint;
  onClick?: () => void;
}

function EndpointCard({ endpoint, onClick }: EndpointCardProps) {
  const isClickable = Boolean(onClick);

  return (
    <div
      className={cn(
        'rounded-md border border-border-subtle bg-muted/30 p-3 transition-colors',
        isClickable
          ? 'cursor-pointer hover:ring-2 hover:ring-sev-5 hover:bg-muted/50'
          : null,
      )}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) onClick?.();
      }}
    >
      <p className="font-semibold mb-2 flex items-center gap-2">
        {endpoint.deviceName || endpoint.deviceIp}
        {isClickable ? (
          <ExternalLink className="w-3 h-3 text-sev-5" />
        ) : null}
      </p>
      {endpoint.deviceName ? (
        <p className="text-xs text-muted-foreground mb-2">{endpoint.deviceIp}</p>
      ) : null}
      {endpoint.interfaceName ? (
        <div className="mt-3">
          <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
            {endpoint.interfaceName}
          </Badge>
          {endpoint.interfaceSpeedMbps ? (
            <p className="text-xs text-muted-foreground mt-1">
              {formatSpeed(endpoint.interfaceSpeedMbps)}
            </p>
          ) : null}
          {endpoint.lagMembership ? (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Member of:</span>
              </p>
              <Badge className="bg-sev-3/15 text-foreground border-0 font-normal mt-1">
                {endpoint.lagMembership.interfaceName}
              </Badge>
            </div>
          ) : null}
          {endpoint.lagMembers && endpoint.lagMembers.length > 0 ? (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">LAG members:</span>
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {endpoint.lagMembers.map((member: InterfaceLagInfo) => (
                  <Badge
                    key={member.interfaceId}
                    variant="secondary"
                    className="font-normal"
                  >
                    {member.interfaceName}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
          {endpoint.vlans && endpoint.vlans.length > 0 ? (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">VLANs:</span>
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {endpoint.vlans
                  .slice()
                  .sort((a, b) => {
                    if (a.isNative && !b.isNative) return -1;
                    if (!a.isNative && b.isNative) return 1;
                    return a.vlanId - b.vlanId;
                  })
                  .map((vlan: InterfaceVlanInfo) => (
                    <Badge
                      key={vlan.vlanId}
                      className={cn('border-0 font-normal', getVlanBadgeClass(vlan))}
                      title={`${vlan.isTagged ? 'Tagged' : 'Untagged'}${vlan.isNative ? ', native VLAN for this port' : ''}`}
                    >
                      {formatVlanName(vlan)}
                    </Badge>
                  ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mt-3">Interface unknown</p>
      )}
    </div>
  );
}

export default function LinkDetailPanel({
  linkData,
  trafficRange,
  onClose,
  onDeviceSelect,
}: LinkDetailPanelProps) {
  if (!linkData) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Select a link to view details</p>
      </div>
    );
  }

  const { sourceEndpoint, targetEndpoint, connectionType, confidenceScore, physical } =
    linkData;

  const linkVlans = (() => {
    const vlanMap = new Map<number, InterfaceVlanInfo>();
    const addVlans = (vlans?: InterfaceVlanInfo[]) => {
      if (vlans) {
        vlans.forEach((v) => {
          const existing = vlanMap.get(v.vlanId);
          if (!existing || v.isNative) vlanMap.set(v.vlanId, v);
        });
      }
    };
    addVlans(sourceEndpoint.vlans);
    addVlans(targetEndpoint.vlans);
    return Array.from(vlanMap.values()).sort((a, b) => {
      if (a.isNative && !b.isNative) return -1;
      if (!a.isNative && b.isNative) return 1;
      return a.vlanId - b.vlanId;
    });
  })();

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold">Link details</h3>
        {onClose ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        ) : null}
      </div>

      <div className="mb-4 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <EndpointCard
          endpoint={sourceEndpoint}
          onClick={onDeviceSelect ? () => { onDeviceSelect(sourceEndpoint); } : undefined}
        />
        <div className="text-center text-muted-foreground">
          <ArrowRight className="w-8 h-8 mx-auto" />
          {physical?.speedBps ? (
            <p className="text-xs text-muted-foreground mt-1">
              {formatSpeed(physical.speedBps / 1_000_000)}
            </p>
          ) : null}
        </div>
        <EndpointCard
          endpoint={targetEndpoint}
          onClick={onDeviceSelect ? () => { onDeviceSelect(targetEndpoint); } : undefined}
        />
      </div>

      <table className="w-full text-sm">
        <tbody className="divide-y divide-border-subtle">
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground py-1.5 w-2/5">
              Discovery method
            </th>
            <td className="py-1.5">{getConnectionTypeLabel(connectionType)}</td>
          </tr>
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
              Confidence
            </th>
            <td className="py-1.5">
              <div className="flex items-center gap-2">
                <Progress value={confidenceScore * 100} className="h-1.5 w-24" />
                <span className="text-xs">{Math.round(confidenceScore * 100)}%</span>
              </div>
            </td>
          </tr>
          {physical?.speedBps ? (
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                Link speed
              </th>
              <td className="py-1.5">{formatSpeed(physical.speedBps / 1_000_000)}</td>
            </tr>
          ) : null}
          {linkVlans.length > 0 ? (
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                VLANs carried
              </th>
              <td className="py-1.5">
                <div className="flex flex-wrap gap-1">
                  {linkVlans.map((vlan) => (
                    <Badge
                      key={vlan.vlanId}
                      className={cn('border-0 font-normal', getVlanBadgeClass(vlan))}
                      title={`${vlan.isTagged ? 'Tagged' : 'Untagged'}${vlan.isNative ? ' (Native)' : ''}`}
                    >
                      {formatVlanName(vlan)}
                    </Badge>
                  ))}
                </div>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      <LinkTrafficSection connectionId={linkData.id} range={trafficRange} />
    </div>
  );
}
